/** Client fallback for genre-aware editor toolbar panels (mirrors backend). */

import { fallbackWidgetBindings } from './widgetBindings';

const CORE_PANELS = ['design', 'settings', 'crm', 'operate'];

const GENRE_LEGACY_PANELS = {
  ecommerce: [],
  restaurant: [],
  health_fitness: ['booking'],
  business: [],
  agency: [],
  realestate: [],
  saas_tech: [],
  blog: [],
  education: [],
  event: [],
  portfolio: [],
  landing: [],
  personal: [],
};

const PANEL_OPERATE_SECTION = {
  store: 'products',
  crm: 'contacts',
  booking: 'bookings',
};

const GENRE_PANEL_OPERATE_SECTION = {
  restaurant: { operate_default: 'bookings' },
  ecommerce: { store: 'products' },
  health_fitness: { booking: 'bookings' },
  business: { crm: 'contacts' },
  agency: { crm: 'leads' },
  realestate: { crm: 'inquiries' },
};

const GENRE_CONTACT_RESOURCE = {
  agency: 'leads',
  landing: 'leads',
  personal: 'messages',
};

function contactResourceForGenre(genreId) {
  return GENRE_CONTACT_RESOURCE[genreId] || 'inquiries';
}

export function fallbackEditorSurfaces(website) {
  const genreId = website?.genre_id || website?.category || 'business';
  const legacy = GENRE_LEGACY_PANELS[genreId] || [];
  const visible = [...CORE_PANELS, ...legacy];
  const sectionMap = GENRE_PANEL_OPERATE_SECTION[genreId] || PANEL_OPERATE_SECTION;

  const contactResource = contactResourceForGenre(genreId);

  const panels = visible.map((id) => {
    if (id === 'operate') {
      return {
        id,
        label: 'Operate',
        kind: 'core',
        operate_section: sectionMap.operate_default || null,
      };
    }
    if (id === 'crm') {
      return {
        id,
        label: 'CRM',
        kind: 'core',
        description: 'Form submissions synced to website CRM',
        operate_section: contactResource,
        synced_with_operate: true,
      };
    }
    if (CORE_PANELS.includes(id)) {
      return { id, label: id.charAt(0).toUpperCase() + id.slice(1), kind: 'core' };
    }
    return {
      id,
      label: id === 'crm' ? 'CRM' : id.charAt(0).toUpperCase() + id.slice(1),
      kind: 'legacy',
      operate_section: sectionMap[id] || PANEL_OPERATE_SECTION[id] || null,
      synced_with_operate: true,
    };
  });

  return {
    genre_id: genreId,
    contact_resource: contactResource,
    widget_bindings: fallbackWidgetBindings(genreId),
    primary_admin: 'operate',
    form_crm_sync_enabled: website?.form_crm_sync_enabled !== false,
    panels,
    visible_panel_ids: visible,
    legacy_panel_ids: legacy,
  };
}

export function isPanelVisible(surfaces, panelId) {
  if (panelId === 'crm') return true;
  if (panelId === 'sitebrain') return surfaces?.visible_panel_ids?.includes('sitebrain') === true;
  if (!surfaces?.visible_panel_ids) return true;
  return surfaces.visible_panel_ids.includes(panelId);
}

export function operateUrl(websiteId, section, view) {
  const base = `/sites/${websiteId}/operate`;
  if (view === 'commerce') {
    const params = new URLSearchParams({ view: 'commerce', commerceTab: section || 'currency' });
    return `${base}?${params.toString()}`;
  }
  return section ? `${base}?section=${encodeURIComponent(section)}` : base;
}

export function crmUrl(websiteId) {
  const params = new URLSearchParams();
  if (websiteId) params.set('website_id', websiteId);
  const qs = params.toString();
  return `/marketing-suite/crm${qs ? `?${qs}` : ''}`;
}

/** Ensure CRM panel exists even when backend editor-surfaces predates core CRM. */
export function ensureCrmInEditorSurfaces(surfaces, website) {
  const base = surfaces || fallbackEditorSurfaces(website);
  if (base.visible_panel_ids?.includes('crm')) return base;

  const genreId = base.genre_id || website?.genre_id || website?.category || 'business';
  const contactResource = base.contact_resource || contactResourceForGenre(genreId);
  const visible = [...(base.visible_panel_ids || []), 'crm'];
  const panels = [
    ...(base.panels || []),
    {
      id: 'crm',
      label: 'CRM',
      kind: 'core',
      description: 'Form submissions synced to website CRM',
      operate_section: contactResource,
      synced_with_operate: true,
    },
  ];

  return {
    ...base,
    contact_resource: contactResource,
    form_crm_sync_enabled: base.form_crm_sync_enabled !== false && website?.form_crm_sync_enabled !== false,
    visible_panel_ids: visible,
    panels,
  };
}
